import { useCallback, useEffect, useRef, useState } from 'react';
import { calculateRms, createSilenceState, sampleSilence, suspendSilence,
  MAX_SAMPLE_GAP_MS, SILENCE_WARNING_MS, SILENCE_RMS_THRESHOLD } from './recordingSilence';
import { restoreSilence, serializeSilence, silenceStorageKey, withSilenceOwnership, type SilenceDiagnostics } from './recordingSilenceStorage';

type MonitorEvent = 'warning' | 'sound-restored' | 'unavailable' | 'dismissed' |
  'restored' | 'saved' | 'reset' | 'storage-unavailable' | 'ownership-unavailable' | 'context-resume-failed';
interface AudioSnapshot {
  analyser: AnalyserNode | null;
  context: AudioContext | null;
  track: MediaStreamTrack | undefined;
  recorder: MediaRecorder | null;
  rotating?: boolean;
}
interface Options {
  enabled: boolean;
  sessionId: string | null;
  storageScope: string | null;
  warningAfterMs?: number;
  readAudio: () => AudioSnapshot;
  onWarning: () => void;
  onClear: () => void;
  onEvent: (event: MonitorEvent, data: { quietElapsedMs: number; reason?: string; diagnostics?: SilenceDiagnostics }) => void;
}
interface Controller {
  key: string;
  reset: () => void;
  finish: () => void;
  dismiss: () => void;
}

export const useRecordingSilenceWarning = (options: Options) => {
  const latest = useRef(options);
  latest.current = options;
  const controller = useRef<Controller | null>(null);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const { enabled, sessionId, storageScope, warningAfterMs = SILENCE_WARNING_MS } = options;
  const key = storageScope && sessionId ? silenceStorageKey(storageScope, sessionId) : null;

  const finish = useCallback(() => {
    if (!key) return;
    if (controller.current?.key === key) {
      controller.current.finish();
      return;
    }
    // A playback-only tab must not erase the active owner's snapshot.
    void withSilenceOwnership(key, new AbortController().signal, async () => {
      try { window.localStorage.removeItem(key); }
      catch {
        try { latest.current.onEvent('storage-unavailable', {
          quietElapsedMs: 0, reason: 'finish-inactive',
        }); } catch { /* Diagnostics are optional. */ }
      }
    }, false).catch(() => { /* An active owner retains its snapshot. */ });
  }, [key]);
  const reset = useCallback(() => {
    if (controller.current?.key === key) controller.current.reset();
    else finish();
  }, [key, finish]);

  const dismiss = useCallback(() => {
    if (controller.current?.key === key) controller.current.dismiss();
  }, [key]);

  useEffect(() => {
    setIsWarningVisible(false);
    if (!enabled || !key || !sessionId) return;
    const abort = new AbortController();
    let disposeOwner: (() => void) | undefined;
    const ownership = withSilenceOwnership(key, abort.signal, () => new Promise<void>((release) => {
      let state = createSilenceState();
      let source: AnalyserNode | null = null;
      let samples = new Uint8Array(0);
      let unavailable = false;
      let rotating = false;
      let finished = false;
      let disposed = false;
      let storageFailed = false;
      let lastSavedAt = -Infinity;
      let lastSaved = '';
      let resumeAttemptedFor: AudioContext | null = null;
      let diagnostics: SilenceDiagnostics = {
        reason: 'starting', observedAt: Date.now(), contextState: null, recorderState: null,
        trackState: null, trackMuted: null, trackEnabled: null, rms: null,
        rmsThreshold: SILENCE_RMS_THRESHOLD, sampleGapMs: null, gapCount: 0,
        lastResetReason: null, lastSoundAt: null,
      };
      const safely = (callback: () => void) => {
        try { callback(); } catch { /* Diagnostics must not interrupt recording. */ }
      };
      const event = (name: MonitorEvent, reason?: string) => safely(() => latest.current.onEvent(name,
        { quietElapsedMs: state.quietElapsedMs, reason, diagnostics: { ...diagnostics } }));
      const storageError = () => {
        if (!storageFailed) event('storage-unavailable');
        storageFailed = true;
      };
      try {
        const raw = window.localStorage.getItem(key);
        const restored = restoreSilence(raw, sessionId, warningAfterMs);
        if (restored) { state = restored; diagnostics.reason = 'restored'; event('restored'); }
        else if (raw) event('reset', 'invalid-snapshot');
      } catch { storageError(); }
      const render = () => setIsWarningVisible(state.phase === 'warning' && !state.dismissed);
      const save = (force = false, reason?: string) => {
        if (finished || disposed) return;
        const now = performance.now();
        const signature = serializeSilence(sessionId, state, 0, diagnostics);
        if (!force && (now - lastSavedAt < 1000 || signature === lastSaved)) return;
        try {
          window.localStorage.setItem(key, serializeSilence(sessionId, state, Date.now(), diagnostics));
          lastSaved = signature;
          lastSavedAt = now;
          storageFailed = false;
          if (reason) event('saved', reason);
        } catch { storageError(); }
      };
      const clearWarning = () => { safely(() => latest.current.onClear()); setIsWarningVisible(false); };
      const owner: Controller = {
        key,
        reset: () => {
          if (finished || disposed) return;
          state = createSilenceState();
          diagnostics = { ...diagnostics, reason: 'user-pause', lastResetReason: 'user-pause', observedAt: Date.now() };
          clearWarning();
          save(true);
          event('reset', 'user-pause');
        },
        dismiss: () => {
          if (finished || disposed || state.phase !== 'warning') return;
          state = { ...state, dismissed: true, toastShown: true };
          clearWarning();
          save(true);
          event('dismissed');
        },
        finish: () => {
          if (finished || disposed) return;
          finished = true;
          clearWarning();
          try { window.localStorage.removeItem(key); } catch { storageError(); }
          event('reset', 'finished');
        },
      };
      controller.current = owner;
      const suspend = (reason: string) => {
        diagnostics.reason = reason;
        state = suspendSilence(state);
        if (!unavailable) { event('unavailable', reason); save(true); }
        else save();
        unavailable = true;
      };
      const resumeContext = (context: AudioContext | null) => {
        if (!context || context.state !== 'suspended' || resumeAttemptedFor === context) return;
        // One attempt per suspension episode; visibility/user gestures may explicitly retry.
        resumeAttemptedFor = context;
        const failed = () => {
          if (!disposed && !finished) event('context-resume-failed');
        };
        try { void context.resume().catch(failed); } catch { failed(); }
      };
      const tick = () => {
        if (disposed || finished || abort.signal.aborted || !latest.current.enabled) return;
        const now = performance.now();
        const audio = latest.current.readAudio();
        const sampleGapMs = state.lastSampleAt === null ? null : now - state.lastSampleAt;
        diagnostics = {
          ...diagnostics, observedAt: Date.now(), contextState: audio.context?.state ?? null,
          recorderState: audio.recorder?.state ?? null, trackState: audio.track?.readyState ?? null,
          trackMuted: audio.track?.muted ?? null, trackEnabled: audio.track?.enabled ?? null,
          rms: null, sampleGapMs,
          gapCount: diagnostics.gapCount + (sampleGapMs !== null &&
            (sampleGapMs > MAX_SAMPLE_GAP_MS || sampleGapMs < 0) ? 1 : 0),
        };
        if (audio.context?.state === 'running') resumeAttemptedFor = null;
        if (audio.rotating) {
          rotating = true;
          diagnostics.reason = 'rotating';
          if (sampleGapMs !== null && sampleGapMs > MAX_SAMPLE_GAP_MS) suspend('rotation-gap');
          else save();
          return;
        }
        if (audio.recorder?.state !== 'recording') { suspend('recorder-inactive'); return; }
        if (!audio.track) { suspend('missing-track'); return; }
        if (audio.track.readyState !== 'live') { suspend('track-ended'); return; }

        let rms: number;
        if (audio.track.muted || !audio.track.enabled) {
          // A LIVE muted/disabled track delivers silence to MediaRecorder (Media Capture spec).
          // It is not a recorder pause, and does not need a running AnalyserNode to confirm silence.
          rms = 0;
          diagnostics.reason = audio.track.muted ? 'muted-track' : 'disabled-track';
        } else {
          if (audio.context?.state !== 'running') {
            resumeContext(audio.context);
            suspend('context-not-running');
            return;
          }
          if (!audio.analyser) { suspend('missing-analyser'); return; }
          if (source !== audio.analyser) {
            if (!rotating) state = suspendSilence(state);
            source = audio.analyser;
            samples = new Uint8Array(source.fftSize);
          }
          try { source!.getByteTimeDomainData(samples); rms = calculateRms(samples); }
          catch { suspend('sample-error'); return; }
          diagnostics.reason = rms > SILENCE_RMS_THRESHOLD ? 'sound' : 'quiet';
        }
        rotating = false;
        diagnostics.rms = rms;
        if (rms > SILENCE_RMS_THRESHOLD) diagnostics.lastSoundAt = Date.now();
        const before = state;
        state = sampleSilence(state, now, rms, warningAfterMs);
        unavailable = false;
        if (before.quietElapsedMs > 0 && state.quietElapsedMs === 0) {
          diagnostics.lastResetReason = 'sound';
          clearWarning();
          event('sound-restored');
          save(true);
        }
        if (state.phase === 'warning' && !state.toastShown && !state.dismissed) {
          state = { ...state, toastShown: true };
          safely(() => latest.current.onWarning());
          event('warning');
          save(true);
        }
        render();
        save();
      };
      const visibility = () => {
        if (!document.hidden) resumeAttemptedFor = null;
        tick();
        save(true, 'visibilitychange');
      };
      const interaction = () => {
        if (disposed || finished || !latest.current.enabled) return;
        const audio = latest.current.readAudio();
        if (audio.recorder?.state !== 'recording' || audio.track?.readyState !== 'live' ||
          audio.track.muted || !audio.track.enabled) return;
        resumeAttemptedFor = null;
        resumeContext(audio.context);
      };
      const pagehide = () => {
        state = suspendSilence(state);
        diagnostics.reason = 'pagehide';
        save(true, 'pagehide');
      };
      render();
      const interval = window.setInterval(tick, 250);
      document.addEventListener('visibilitychange', visibility);
      document.addEventListener('pointerdown', interaction);
      document.addEventListener('keydown', interaction);
      window.addEventListener('pagehide', pagehide);
      disposeOwner = () => {
        if (disposed) return;
        save(true, 'cleanup');
        disposed = true;
        window.clearInterval(interval);
        document.removeEventListener('visibilitychange', visibility);
        document.removeEventListener('pointerdown', interaction);
        document.removeEventListener('keydown', interaction);
        window.removeEventListener('pagehide', pagehide);
        if (controller.current === owner) controller.current = null;
        safely(() => latest.current.onClear());
        release();
      };
      tick();
    }), true, () => {
      try { latest.current.onEvent('ownership-unavailable', { quietElapsedMs: 0 }); }
      catch { /* Diagnostics are optional. */ }
    });
    void ownership.catch(() => {
      if (!abort.signal.aborted) {
        try { latest.current.onEvent('ownership-unavailable', { quietElapsedMs: 0 }); }
        catch { /* Diagnostics are optional. */ }
      }
    });
    return () => { abort.abort(); disposeOwner?.(); };
  }, [enabled, key, sessionId, warningAfterMs]);

  return { isWarningVisible, dismiss, reset, finish };
};
